import {
  amendDriver,
  changeDriverGroupAssignments,
  clearDrivers,
  createDriver,
  editDriver,
  importDrivers,
  loadDriverDetails,
  loadDrivers,
  loadDriversCsv,
  loadDriversGroups,
  loadDriverOrphans,
  removeFromDrivers,
  removeSelectedDriver,
  removeSelectedDriverGroups,
  resetCurrentStep,
  saveCurrentStep,
  selectDriver,
  setCreatingNewDriver,
  setDriverErrorMessage,
  setDriversOffset,
  setDriversQuery,
  setDriversRowsDescriptor,
  setDriversSort,
  setPrefilledDriverId,
  triggerTableReload,
  setDriverOrphans,
  clearImportResponse,
  loadLinkedData,
} from './drivers.actions';
import {
  clearSelectedDriver,
  clearDriverStore,
  trackDriversRequestsStatus,
  updateCurrentStepReset,
  updateCurrentStepSave,
  updateHasDrivers,
  updateTableReload,
  updateDriverErrorMessage,
  updateDrivers,
  driverStoreSnapshot,
  updateSelectedDriver,
  updateCreatingNewDriver,
  updateDriverUpdateStatus,
  resetDriversStatus,
  updateSelectedDriversGroupIds,
  updateSelectedDriversGroups,
  updateDeleteDriverStatus,
  updateDriversGroupsStatus,
  clearSelectedDriverGroups,
  updateOffset,
  updateSort,
  updateQuery,
  updateDriversRowsDescriptor,
  driverEntitiesSnapshot,
  updatePrefilledDriverId,
  updateOrphans,
  trackDriversRequests,
  updateImportResponse,
  updateDriverImportStatus,
  updateLinkedData,
} from './drivers.repository';
import { reloadAssets } from '../assets/assets.actions';
import { driverIntegrationLinkedDataEnabled } from '../app-settings/app-settings.repository';
import { chimportDrivers } from '../utils/chimport-drivers';
import { checkHasAssets } from '../ams/ams.actions';
import { DriversImportResponse } from '../../models/drivers-import-response';
import { EMPTY_ROWS_DESCRIPTOR } from '../../models/empty-rows-descriptor';
import { boolDefinedAndTrue } from '../../utils/bool-defined-and-true';
import { downloadFile } from '../../utils/download-file';
import { getErrorContext } from '../../utils/get-error-context';
import { reportToTrackjs } from '../../utils/report-to-trackjs';
import { updateToast } from '../toasts.repository';
import { extractAndHandleError } from '../../utils/extract-and-handle-error';
import { translate } from '@jsverse/transloco';
import {
  Driver,
  DriverVehicleLink,
  FleetApi,
  OrphanedResource,
} from '@tmx/data-access';
import { iif, switchMap, tap } from 'rxjs';
import {
  createEffect,
  dispatch,
  ofType,
  registerEffects,
  removeEffects,
  tapResult,
} from '@ngneat/effects';
import {
  LoadingRowsDescriptor,
  RowDescriptor,
  SelectAllState,
  UpdateRowsDescriptor,
} from '@tmx/operandi';

const TRACKJS_SOURCE = 'driver effects';
const API = 'fleet';

function displayDriverError(error: string) {
  dispatch(
    setDriverErrorMessage({
      error,
    })
  );
}

const loadDrivers$ = createEffect((actions) =>
  actions.pipe(
    ofType(loadDrivers),
    switchMap(({ params }) => {
      return FleetApi.getDrivers(params).pipe(
        trackDriversRequestsStatus('drivers'),
        tapResult(
          ({ data, size, unfilteredSize }) => {
            updateHasDrivers(unfilteredSize > 0);
            if (driverIntegrationLinkedDataEnabled()) {
              dispatch(
                loadLinkedData({
                  driverIds: data.map((driver) => driver.driverId),
                })
              );
            }
            updateDrivers(data, size);
          },
          (error) => {
            reportToTrackjs(
              TRACKJS_SOURCE,
              'Error loading drivers',
              getErrorContext(error)
            );
            extractAndHandleError(error, API, displayDriverError);
          }
        )
      );
    })
  )
);

const loadLinkedData$ = createEffect((actions) =>
  actions.pipe(
    ofType(loadLinkedData),
    switchMap(({ driverIds }) =>
      FleetApi.postDriversVehicleLinks(driverIds).pipe(
        trackDriversRequestsStatus('linked-data'),
        tapResult(({ data }) => {
          const linkedData = new Map<string, DriverVehicleLink>();
          for (const link of data.driverVehicleLinks) {
            linkedData.set(link.driverId, link);
          }
          updateLinkedData(linkedData);
        })
      )
    )
  )
);

const loadDriversCsv$ = createEffect((actions) =>
  actions.pipe(
    ofType(loadDriversCsv),
    switchMap(() => {
      return FleetApi.getDriversExport().pipe(
        trackDriversRequests(['fetch-csv']),
        tapResult(
          (response) => {
            downloadFile(response, 'Drivers.csv');
            updateToast({
              type: 'success',
              content: translate('cfs.drivers.exportedDrivers'),
            });
          },
          (error) => {
            updateToast({
              type: 'danger',
              content: translate('cfs.drivers.failedToExportDrivers'),
            });
            reportToTrackjs(
              TRACKJS_SOURCE,
              'Error exporting drivers',
              getErrorContext(error)
            );
          }
        )
      );
    })
  )
);

const setDriversOffset$ = createEffect((actions) =>
  actions.pipe(
    ofType(setDriversOffset),
    tap(({ offset }) => {
      updateOffset(offset);
    })
  )
);

const setDriversSort$ = createEffect((actions) =>
  actions.pipe(
    ofType(setDriversSort),
    tap(({ sortExpression, sortDirection }) => {
      updateSort(sortExpression, sortDirection);
    })
  )
);

const setDriversQuery$ = createEffect((actions) =>
  actions.pipe(
    ofType(setDriversQuery),
    tap(({ query }) => {
      updateQuery(query);
    })
  )
);

const setDriversRowsDescriptor$ = createEffect((actions) =>
  actions.pipe(
    ofType(setDriversRowsDescriptor),
    tap(({ loading }) => {
      if (loading) {
        updateDriversRowsDescriptor(new LoadingRowsDescriptor());
        return;
      }
      const { totalDrivers, offset } = driverStoreSnapshot();
      const drivers = driverEntitiesSnapshot();
      const rows = drivers.map(
        (driver) => new RowDescriptor(driver, (row) => row.data.id)
      );
      updateDriversRowsDescriptor(
        totalDrivers === 0
          ? EMPTY_ROWS_DESCRIPTOR
          : new UpdateRowsDescriptor(
              rows,
              offset,
              totalDrivers,
              0,
              SelectAllState.Deselected
            )
      );
    })
  )
);

const selectDriver$ = createEffect((actions) =>
  actions.pipe(
    ofType(selectDriver),
    tap(({ driver }) => {
      updateSelectedDriver(driver);
    })
  )
);

const resetCurrentStep$ = createEffect((actions) =>
  actions.pipe(
    ofType(resetCurrentStep),
    tap(({ currentStep }) => {
      updateCurrentStepReset(currentStep);
    })
  )
);

const saveCurrentStep$ = createEffect((actions) =>
  actions.pipe(
    ofType(saveCurrentStep),
    tap(({ currentStep }) => {
      updateCurrentStepSave(currentStep);
    })
  )
);

const loadDriversGroups$ = createEffect((actions) =>
  actions.pipe(
    ofType(loadDriversGroups),
    switchMap(({ driverId }) =>
      FleetApi.getDriversDriverGroups(driverId).pipe(
        trackDriversRequestsStatus('drivers-groups'),
        tapResult(({ data }) => {
          updateSelectedDriversGroups(data);
          updateSelectedDriversGroupIds(
            data.map((driverGroup) => driverGroup.driverGroupId)
          );
        })
      )
    )
  )
);

export const changeDriverGroupAssignments$ = createEffect((actions) =>
  actions.pipe(
    ofType(changeDriverGroupAssignments),
    tap(() => {
      dispatch(setDriverErrorMessage({ error: null }));
    }),
    switchMap(
      ({ assignmentDriverGroupIds, unassignmentDriverGroupIds, id }) => {
        return FleetApi.postDriversDriverGroupsCombinedAssignment(
          id,
          assignmentDriverGroupIds,
          unassignmentDriverGroupIds
        ).pipe(
          trackDriversRequestsStatus('edit-drivers-groups'),
          tapResult(
            () => {
              updateDriversGroupsStatus();
              const currentIds = driverStoreSnapshot().selectedDriversGroupIds;
              currentIds.push(...assignmentDriverGroupIds);
              const updatedIds = currentIds.filter(
                (currentId) => !unassignmentDriverGroupIds.includes(currentId)
              );
              updateSelectedDriversGroupIds(updatedIds);
              dispatch(triggerTableReload());
              updateToast({
                type: 'success',
                content: translate(
                  'cfs.addEditDriverModal.toastSuccessUpdateDriver'
                ),
              });
            },
            (error: Response) => {
              reportToTrackjs(
                TRACKJS_SOURCE,
                'Error assigning driver group',
                getErrorContext(error)
              );
              if (error.status === 500) {
                displayDriverError(
                  translate('cfs.assignDriverToDriverGroup.genericError')
                );
              } else {
                extractAndHandleError(error, API, displayDriverError);
              }
            }
          )
        );
      }
    )
  )
);

export const amendDriver$ = createEffect((actions) =>
  actions.pipe(
    ofType(amendDriver),
    tap(() => {
      dispatch(setDriverErrorMessage({ error: null }));
    }),
    switchMap(({ driver, unassignmentDriverGroupIds }) => {
      return FleetApi.postDriversDriverGroupsCombinedAssignment(
        driver.id,
        [],
        unassignmentDriverGroupIds
      ).pipe(
        trackDriversRequestsStatus('edit-drivers-groups'),
        tapResult(
          () => {
            updateDriversGroupsStatus();
            dispatch(removeSelectedDriverGroups());
            dispatch(removeFromDrivers({ driverId: driver.driverId }));
          },
          (error: Response) => {
            reportToTrackjs(
              TRACKJS_SOURCE,
              'Error deleting driver',
              getErrorContext(error)
            );
            updateToast({
              type: 'danger',
              content: translate('cfs.drivers.toastFailureDeleteDriver'),
            });
          }
        )
      );
    })
  )
);

const removeFromDriver$ = createEffect((actions) =>
  actions.pipe(
    ofType(removeFromDrivers),
    switchMap(({ driverId }) => {
      return FleetApi.deleteDrivers(encodeURIComponent(driverId)).pipe(
        trackDriversRequestsStatus('delete-driver'),
        tapResult(
          () => {
            updateToast({
              type: 'success',
              content: translate('cfs.drivers.toastSuccessDeleteDriver'),
            });
            updateDeleteDriverStatus();
            dispatch(triggerTableReload());
          },
          (error) => {
            reportToTrackjs(
              TRACKJS_SOURCE,
              'Error deleting Driver',
              getErrorContext(error)
            );
            extractAndHandleError(error, API, displayDriverError);
          }
        )
      );
    })
  )
);

const clearDrivers$ = createEffect((actions) =>
  actions.pipe(
    ofType(clearDrivers),
    tapResult(() => {
      clearDriverStore();
    })
  )
);

const createDriver$ = createEffect((actions) =>
  actions.pipe(
    ofType(createDriver),
    switchMap(({ drivers, skipSelection }) =>
      FleetApi.postDrivers(drivers).pipe(
        trackDriversRequestsStatus('driver-update'),
        tapResult(
          (response) => {
            const { data } = response;
            if (!driverStoreSnapshot().hasDrivers) {
              dispatch(checkHasAssets());
            }
            const selectedDriver = boolDefinedAndTrue(skipSelection)
              ? null
              : data[0];
            updateSelectedDriver(selectedDriver);
            updateDriverUpdateStatus();
            resetDriversStatus();
            dispatch(triggerTableReload());
            dispatch(reloadAssets());
            updateToast({
              type: 'success',
              content: translate(
                'cfs.addEditDriverModal.toastSuccessCreateDriver'
              ),
            });
          },
          (error) => {
            reportToTrackjs(
              TRACKJS_SOURCE,
              'Error creating driver',
              getErrorContext(error)
            );
            extractAndHandleError(error, API, displayDriverError);
          }
        )
      )
    )
  )
);

const importDrivers$ = createEffect((actions) =>
  actions.pipe(
    ofType(importDrivers),
    trackDriversRequestsStatus('driver-import'),
    tap(() => {
      dispatch(clearImportResponse());
    }),
    chimportDrivers(TRACKJS_SOURCE),
    tapResult((importResponse) => {
      updateDriverImportStatus();
      updateImportResponse(importResponse);
      if (importResponse.imported > 0) {
        dispatch(triggerTableReload());
      }
    })
  )
);

const clearImportResponse$ = createEffect((actions) =>
  actions.pipe(
    ofType(clearImportResponse),
    tap(() => {
      updateImportResponse(new DriversImportResponse());
    })
  )
);

const editDriver$ = createEffect((actions) =>
  actions.pipe(
    ofType(editDriver),
    switchMap(({ drivers, integrationType, skipSelection }) =>
      iif(
        () => integrationType === 'DriverId',
        FleetApi.putDrivers(drivers).pipe(
          trackDriversRequestsStatus('driver-update')
        ),
        FleetApi.putDriversUpdateByIds(drivers).pipe(
          trackDriversRequestsStatus('driver-update')
        )
      ).pipe(
        tapResult(
          (response) => {
            const { data } = response;

            const selectedDriver = boolDefinedAndTrue(skipSelection)
              ? null
              : data[0];
            updateSelectedDriver(selectedDriver as Driver);
            updateDriverUpdateStatus();
            resetDriversStatus();
            dispatch(triggerTableReload());
            dispatch(reloadAssets());
            updateToast({
              type: 'success',
              content: translate(
                'cfs.addEditDriverModal.toastSuccessUpdateDriver'
              ),
            });
          },
          (error) => {
            reportToTrackjs(
              TRACKJS_SOURCE,
              'Error updating driver',
              getErrorContext(error)
            );
            extractAndHandleError(error, API, displayDriverError);
          }
        )
      )
    )
  )
);

const loadDriverDetails$ = createEffect((actions) =>
  actions.pipe(
    ofType(loadDriverDetails),
    switchMap(({ driverId }) =>
      FleetApi.getDriversId(driverId as unknown as string).pipe(
        trackDriversRequestsStatus('driver-details'),
        tapResult(
          (response) => {
            updateSelectedDriver(response);
          },
          (error) => {
            reportToTrackjs(
              TRACKJS_SOURCE,
              'Error loading driver details',
              getErrorContext(error)
            );
            updateToast({
              type: 'danger',
              content: translate(
                'cfs.addEditDriverModal.toastErrorLoadingDriverDetails'
              ),
            });
          }
        )
      )
    )
  )
);

const removeSelectedDriver$ = createEffect((actions) =>
  actions.pipe(
    ofType(removeSelectedDriver),
    tapResult(() => {
      clearSelectedDriver();
    })
  )
);

const removeSelectedDriverGroups$ = createEffect((actions) =>
  actions.pipe(
    ofType(removeSelectedDriverGroups),
    tapResult(() => {
      clearSelectedDriverGroups();
    })
  )
);

const setDriverErrorMessage$ = createEffect((actions) =>
  actions.pipe(
    ofType(setDriverErrorMessage),
    tap(({ error }) => {
      const { selectedDriver } = driverStoreSnapshot();
      if (error !== null) {
        if (!selectedDriver) {
          updateToast({ type: 'danger', content: error });
        }
      }
      updateDriverErrorMessage(error);
    })
  )
);

const setCreatingNewDriver$ = createEffect((actions) =>
  actions.pipe(
    ofType(setCreatingNewDriver),
    tap(({ creatingNewDriver }) => {
      updateCreatingNewDriver(creatingNewDriver);
    })
  )
);

const setPrefilledDriverId$ = createEffect((actions) =>
  actions.pipe(
    ofType(setPrefilledDriverId),
    tap(({ id }) => {
      updatePrefilledDriverId(id);
    })
  )
);

const loadDriverOrphans$ = createEffect((actions) =>
  actions.pipe(
    ofType(loadDriverOrphans),
    switchMap(() =>
      FleetApi.getCompanyFleetsOrphan().pipe(
        tapResult(
          (orphanedDrivers) => {
            const orphans = new Map<string, OrphanedResource>();
            for (const driver of orphanedDrivers) {
              orphans.set(driver.vehicleId ?? '', driver);
            }
            updateOrphans(orphans);
          },
          (error: Response) => {
            if (error.status === 404) {
              updateOrphans(new Map<string, OrphanedResource>());
            } else {
              reportToTrackjs(
                TRACKJS_SOURCE,
                'Error loading orphans',
                getErrorContext(error)
              );
              extractAndHandleError(error, API, displayDriverError);
            }
          }
        )
      )
    )
  )
);

const setDriverOrphans$ = createEffect((actions) =>
  actions.pipe(
    ofType(setDriverOrphans),
    tap(({ orphans }) => {
      updateOrphans(orphans);
    })
  )
);

const triggerTableReload$ = createEffect((actions) =>
  actions.pipe(
    ofType(triggerTableReload),
    tap(() => {
      updateTableReload();
    })
  )
);

const effects = [
  loadDrivers$,
  loadLinkedData$,
  selectDriver$,
  loadDriversCsv$,
  setDriversOffset$,
  setDriversSort$,
  setDriversQuery$,
  setDriversRowsDescriptor$,
  changeDriverGroupAssignments$,
  removeFromDriver$,
  editDriver$,
  removeSelectedDriverGroups$,
  amendDriver$,
  createDriver$,
  importDrivers$,
  clearImportResponse$,
  loadDriversGroups$,
  loadDriverDetails$,
  setCreatingNewDriver$,
  setPrefilledDriverId$,
  loadDriverOrphans$,
  setDriverOrphans$,
  clearDrivers$,
  saveCurrentStep$,
  resetCurrentStep$,
  setDriverErrorMessage$,
  removeSelectedDriver$,
  triggerTableReload$,
];

export function registerDriversEffects() {
  registerEffects(effects);
}

export function deregisterDriversEffects() {
  removeEffects(effects);
}
